import { useState, useCallback, useEffect, useRef } from 'react';
import { useMutation } from '@apollo/client';
import { UPDATE_CHECKLIST, CREATE_CHECKLIST } from '../graphql/checklist';

// Stable ID generator to avoid React re-render issues
const generateStableId = (item, index) => {
  if (typeof item.id === 'string' && item.id.includes('-')) {
    return item.id;
  }
  return `item-${index}-${item.title.replace(/\s+/g, '-').toLowerCase()}`;
};

export default function ChecklistModal({ checklist, categories, onClose, onSave, onDelete }) {
  const [title, setTitle] = useState(checklist?.title || '');
  
  // Create stable items with consistent IDs
  const [items, setItems] = useState(() => {
    if (!checklist?.items) return [];
    
    return checklist.items.map((item, index) => ({
      ...item,
      id: String(item.id), // Ensure ID is always a string
      stableId: generateStableId(item, index), // Create stable ID for drag
      title: item.title,
      categoryId: item.categoryId,
      sortOrder: item.sortOrder || index,
      type: item.type || 'TEXT',
      isRequired: item.isRequired ?? true,
      isEditing: false // Add editing state for each item
    }));
  });
  
  const [newItem, setNewItem] = useState('');
  const [newItemCategory, setNewItemCategory] = useState(categories[0]?.id || '');
  const [isAutoSaving, setIsAutoSaving] = useState(false);

  const [updateChecklist] = useMutation(UPDATE_CHECKLIST);
  const [createChecklist] = useMutation(CREATE_CHECKLIST);

  // Debounced title auto-save
  const titleSaveTimeoutRef = useRef(null);
  
  useEffect(() => {
    // Clear existing timeout
    if (titleSaveTimeoutRef.current) {
      clearTimeout(titleSaveTimeoutRef.current);
    }

    // Only auto-save title for existing checklists and if title has changed
    if (checklist?.id && title && title !== checklist.title) {
      titleSaveTimeoutRef.current = setTimeout(async () => {
        setIsAutoSaving(true);
        try {
          await updateChecklist({
            variables: {
              input: {
                id: checklist.id,
                title: title
              }
            }
          });
        } catch (error) {
          console.error('Auto-save title failed:', error);
        } finally {
          setIsAutoSaving(false);
        }
      }, 1000); // 1 second debounce
    }

    // Cleanup timeout on unmount
    return () => {
      if (titleSaveTimeoutRef.current) {
        clearTimeout(titleSaveTimeoutRef.current);
      }
    };
  }, [title, checklist?.id, checklist?.title, updateChecklist]);

  // Prevent accidental navigation away from unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Only warn if there are unsaved changes
      if (title.trim() && items.length > 0) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [title, items]);

  // Auto-save function for existing checklists
  const autoSaveChecklist = useCallback(async (checklistData) => {
    if (!checklist?.id) return; // Only auto-save for existing checklists
    
    try {
      await updateChecklist({
        variables: {
          input: {
            id: checklist.id,
            title: checklistData.title || title,
            description: checklistData.description || '',
            updatedItems: checklistData.updatedItems || []
          }
        }
      });
    } catch (error) {
      console.error('Auto-save failed:', error);
      // Don't show error to user for auto-save failures
    }
  }, [checklist?.id, title, updateChecklist]);

  // Auto-save function for new checklists (create if doesn't exist)
  const autoSaveNewChecklist = useCallback(async (checklistData) => {
    if (checklist?.id) return; // Only for new checklists
    
    try {
      const result = await createChecklist({
        variables: {
          input: {
            title: checklistData.title || title,
            description: checklistData.description || '',
            items: checklistData.items || []
          }
        }
      });
      
      // Update the checklist ID so future auto-saves work
      if (result.data?.createChecklist?.id) {
        // We'll need to update the parent component with the new ID
        // For now, we'll handle this in the main save function
        return result.data.createChecklist.id;
      }
    } catch (error) {
      console.error('Auto-save new checklist failed:', error);
    }
    return null;
  }, [checklist?.id, title, createChecklist]);

  const handleAddItem = useCallback(async () => {
    if (newItem.trim() && newItemCategory) {
      const timestamp = Date.now();
      const newItemData = { 
        id: `temp-${timestamp}`,
        title: newItem.trim(),
        categoryId: newItemCategory,
        sortOrder: items.length,
        type: 'TEXT',
        isRequired: true,
        isEditing: false
      };
      
      setItems(prevItems => [...prevItems, newItemData]);
      setNewItem('');

      // Auto-save the new item if this is an existing checklist
      if (checklist?.id) {
        const newItems = [newItemData].map(item => ({
          title: item.title,
          categoryId: item.categoryId,
          sortOrder: item.sortOrder,
          type: item.type || 'TEXT',
          isRequired: item.isRequired ?? true
        }));

        await updateChecklist({
          variables: {
            input: {
              id: checklist.id,
              title: title,
              newItems: newItems
            }
          }
        });
      }
      // Note: For new checklists, auto-save will work after the first manual save
    }
  }, [newItem, newItemCategory, items, checklist?.id, title, updateChecklist, createChecklist]);

  const handleRemoveItem = useCallback((itemId) => {
    setItems(prevItems => prevItems.filter(item => item.id !== itemId));
  }, []);

  const handleUpdateItemCategory = useCallback((itemId, categoryId) => {
    setItems(prevItems => prevItems.map(item => 
      item.id === itemId ? { ...item, categoryId } : item
    ));
  }, []);

  const handleStartEditingItem = useCallback((itemId) => {
    setItems(prevItems => prevItems.map(item => 
      item.id === itemId ? { ...item, isEditing: true } : { ...item, isEditing: false }
    ));
  }, []);

  const handleUpdateItemText = useCallback((itemId, newText) => {
    setItems(prevItems => prevItems.map(item => 
      item.id === itemId ? { ...item, title: newText.trim() } : item
    ));
  }, []);

  const handleSaveItemEdit = useCallback(async (itemId) => {
    setItems(prevItems => prevItems.map(item => 
      item.id === itemId ? { ...item, isEditing: false } : item
    ));

    // Auto-save to backend if editing existing checklist
    if (checklist?.id) {
      const updatedItems = items
        .filter(item => !item.id.startsWith('temp-'))
        .map(item => ({
          id: item.id,
          title: item.title,
          categoryId: item.categoryId,
          sortOrder: item.sortOrder,
          type: item.type || 'TEXT',
          isRequired: item.isRequired ?? true
        }));

      updateChecklist({
        variables: {
          input: {
            id: checklist.id,
            title: title,
            updatedItems: updatedItems
          }
        }
      }).catch(error => {
        console.error('Auto-save failed during item edit:', error);
      });
    }
  }, [checklist?.id, title, items, updateChecklist]);

  const handleCancelItemEdit = useCallback((itemId, originalText) => {
    setItems(prevItems => prevItems.map(item => 
      item.id === itemId ? { ...item, title: originalText, isEditing: false } : item
    ));
  }, []);

  // Move item up in the list
  const handleMoveItemUp = useCallback(async (index) => {
    if (index === 0) return; // Can't move first item up
    
    setItems(currentItems => {
      const newItems = Array.from(currentItems);
      // Swap with previous item
      [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
      
      // Update sortOrder
      const updatedItems = newItems.map((item, newIndex) => ({
        ...item,
        sortOrder: newIndex
      }));

      // Auto-save to backend if editing existing checklist
      if (checklist?.id) {
        const itemsToUpdate = updatedItems
          .filter(item => !item.id.startsWith('temp-'))
          .map(item => ({
            id: item.id,
            title: item.title,
            categoryId: item.categoryId,
            sortOrder: item.sortOrder,
            type: item.type || 'TEXT',
            isRequired: item.isRequired ?? true
          }));

        updateChecklist({
          variables: {
            input: {
              id: checklist.id,
              title: title,
              updatedItems: itemsToUpdate
            }
          }
        }).catch(error => {
          console.error('Auto-save failed during move:', error);
        });
      }

      return updatedItems;
    });
  }, [checklist?.id, title, updateChecklist]);

  // Move item down in the list
  const handleMoveItemDown = useCallback(async (index) => {
    setItems(currentItems => {
      if (index === currentItems.length - 1) return currentItems; // Can't move last item down
      
      const newItems = Array.from(currentItems);
      // Swap with next item
      [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
      
      // Update sortOrder
      const updatedItems = newItems.map((item, newIndex) => ({
        ...item,
        sortOrder: newIndex
      }));

      // Auto-save to backend if editing existing checklist
      if (checklist?.id) {
        const itemsToUpdate = updatedItems
          .filter(item => !item.id.startsWith('temp-'))
          .map(item => ({
            id: item.id,
            title: item.title,
            categoryId: item.categoryId,
            sortOrder: item.sortOrder,
            type: item.type || 'TEXT',
            isRequired: item.isRequired ?? true
          }));

        updateChecklist({
          variables: {
            input: {
              id: checklist.id,
              title: title,
              updatedItems: itemsToUpdate
            }
          }
        }).catch(error => {
          console.error('Auto-save failed during move:', error);
        });
      }

      return updatedItems;
    });
  }, [checklist?.id, title, updateChecklist]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (title.trim() && items.length > 0) {
      if (checklist?.id) {
        // Update existing checklist
        const existingItemIds = checklist.items?.map(item => item.id) || [];
        const currentItemIds = items.map(item => item.id);
        
        // Find deleted items
        const deletedItemIds = existingItemIds.filter(id => !currentItemIds.includes(id));
        
        // Separate new and updated items
        const newItems = [];
        const updatedItems = [];
        
        items.forEach(item => {
          const formattedItem = {
            title: item.title,
            categoryId: item.categoryId || categories[0]?.id,
            sortOrder: item.sortOrder,
            type: item.type || 'TEXT',
            isRequired: item.isRequired ?? true
          };

          if (item.id.startsWith('temp-')) {
            newItems.push(formattedItem);
          } else {
            updatedItems.push({
              id: item.id,
              ...formattedItem
            });
          }
        });

        onSave({
          id: checklist.id,
          title,
          newItems,
          updatedItems,
          deletedItemIds
        });
      } else {
        // Create new checklist - this should be rare since we auto-save now
        onSave({
          title,
          items: items.map(item => ({
            title: item.title,
            categoryId: item.categoryId || categories[0]?.id,
            sortOrder: item.sortOrder,
            type: item.type || 'TEXT',
            isRequired: item.isRequired ?? true
          }))
        });
      }
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this checklist? This action cannot be undone.')) {
      await onDelete(checklist.id);
      onClose();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && newItem.trim() && newItemCategory) {
      e.preventDefault();
      handleAddItem();
    }
  };

  return (
    <div className="fixed inset-0 z-10 overflow-y-auto">
      <div className="min-h-screen px-4 text-center">
        <div className="fixed inset-0 bg-black opacity-30" onClick={onClose} />

        <span className="inline-block h-screen align-middle">&#8203;</span>

        <div className="inline-block w-full max-w-2xl p-6 my-8 text-left align-middle bg-white shadow-xl rounded-2xl relative">
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-medium text-gray-900">
                  {checklist ? 'Edit Checklist' : 'Create New Checklist'}
                </h3>
                {isAutoSaving && (
                  <div className="flex items-center gap-1 text-xs text-blue-600">
                    <div className="w-3 h-3 border border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    <span>Auto-saving...</span>
                  </div>
                )}
                {checklist?.id && !isAutoSaving && title && title !== checklist.title && (
                  <div className="text-xs text-green-600">
                    ✓ Auto-saved
                  </div>
                )}
                {!checklist?.id && title.trim() && (
                  <div className="text-xs text-blue-600">
                    💾 Auto-save available after first save
                  </div>
                )}
              </div>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Checklist Title"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                required
              />
            </div>

            <div className="mb-4">
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Add new item"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
                <select
                  value={newItemCategory}
                  onChange={(e) => setNewItemCategory(e.target.value)}
                  className="w-48 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                  required
                >
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.title}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleAddItem}
                  disabled={!newItem.trim() || !newItemCategory}
                  className="px-4 py-2 text-white bg-sky-600 rounded-md hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50"
                >
                  Add
                </button>
              </div>

              <div className="mt-4 max-h-[400px] overflow-y-auto">
                <div className="space-y-2 min-h-[50px] p-2">
                  {items.map((item, index) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 p-3 bg-white border-2 rounded-lg transition-all duration-200 border-gray-200 hover:shadow-md hover:border-gray-300"
                    >
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => handleMoveItemUp(index)}
                          disabled={index === 0}
                          className={`w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 transition-colors ${
                            index === 0 
                              ? 'text-gray-300 cursor-not-allowed' 
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                          title="Move up"
                        >
                          <svg width="12" height="12" viewBox="0 0 12 12" className="fill-current">
                            <path d="M6 2L2 6h3v4h2V6h3L6 2z"/>
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMoveItemDown(index)}
                          disabled={index === items.length - 1}
                          className={`w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 transition-colors ${
                            index === items.length - 1 
                              ? 'text-gray-300 cursor-not-allowed' 
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                          title="Move down"
                        >
                          <svg width="12" height="12" viewBox="0 0 12 12" className="fill-current">
                            <path d="M6 10l4-4H7V2H5v4H2l4 4z"/>
                          </svg>
                        </button>
                      </div>
                      <div className="flex-1 min-w-0">
                        {item.isEditing ? (
                          <input
                            type="text"
                            value={item.title}
                            onChange={(e) => handleUpdateItemText(item.id, e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSaveItemEdit(item.id);
                              } else if (e.key === 'Escape') {
                                e.preventDefault();
                                handleCancelItemEdit(item.id, checklist?.items?.find(orig => orig.id === item.id)?.title || item.title);
                              }
                            }}
                            onBlur={() => handleSaveItemEdit(item.id)}
                            autoFocus
                            className="w-full text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-sky-500"
                          />
                        ) : (
                          <span 
                            className="text-sm font-medium text-gray-900 block truncate cursor-pointer hover:text-sky-600"
                            onClick={() => handleStartEditingItem(item.id)}
                            title="Click to edit"
                          >
                            {item.title}
                          </span>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        <select
                          value={item.categoryId}
                          onChange={(e) => handleUpdateItemCategory(item.id, e.target.value)}
                          className="w-[180px] px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-sky-500"
                          onClick={(e) => e.stopPropagation()}
                          disabled={item.isEditing}
                        >
                          {categories.map(category => (
                            <option key={category.id} value={category.id}>
                              {category.title}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Edit/Save/Cancel buttons */}
                      <div className="flex-shrink-0 flex items-center gap-1">
                        {item.isEditing ? (
                          <>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSaveItemEdit(item.id);
                              }}
                              className="w-7 h-7 flex items-center justify-center text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-all"
                              title="Save changes"
                            >
                              <svg width="14" height="14" viewBox="0 0 14 14" className="fill-current">
                                <path d="M11.854 3.146a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L4.5 9.793l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancelItemEdit(item.id, checklist?.items?.find(orig => orig.id === item.id)?.title || item.title);
                              }}
                              className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded transition-all"
                              title="Cancel editing"
                            >
                              <svg width="14" height="14" viewBox="0 0 14 14" className="fill-current">
                                <path d="M7 5.586L11.293 1.293a1 1 0 111.414 1.414L8.414 7l4.293 4.293a1 1 0 01-1.414 1.414L7 8.414l-4.293 4.293a1 1 0 01-1.414-1.414L5.586 7 1.293 2.707a1 1 0 011.414-1.414L7 5.586z"/>
                              </svg>
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartEditingItem(item.id);
                            }}
                            className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded transition-all"
                            title="Edit item text"
                          >
                            <svg width="14" height="14" viewBox="0 0 14 14" className="fill-current">
                              <path d="M11.013 2.987a1.5 1.5 0 0 1 0 2.122l-7.5 7.5a.5.5 0 0 1-.177.119l-3 1a.5.5 0 0 1-.642-.642l1-3a.5.5 0 0 1 .119-.177l7.5-7.5a1.5 1.5 0 0 1 2.122 0z"/>
                            </svg>
                          </button>
                        )}
                        
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveItem(item.id);
                          }}
                          className="w-7 h-7 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                          title="Remove item"
                          disabled={item.isEditing}
                        >
                          <svg width="14" height="14" viewBox="0 0 14 14" className="fill-current">
                            <path d="M7 5.586L11.293 1.293a1 1 0 111.414 1.414L8.414 7l4.293 4.293a1 1 0 01-1.414 1.414L7 8.414l-4.293 4.293a1 1 0 01-1.414-1.414L5.586 7 1.293 2.707a1 1 0 011.414-1.414L7 5.586z"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                  {items.length === 0 && (
                    <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
                      No items added yet. Add your first checklist item above.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              {checklist && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="px-4 py-2 text-red-600 bg-red-50 rounded-md hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Delete
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!title.trim() || items.length === 0}
                className="px-4 py-2 text-white bg-sky-600 rounded-md hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}